import React, { useCallback, useEffect } from "react";
import { Nav, Navbar, NavbarToggler } from "reactstrap";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ErrorMessage, SuccessMessage } from "../../_UI/Components/Alerts/PathwayAlertMessages";
import useEnvironment from "../../EnvironmentUtils";
import styled from "styled-components";
import useUserAuthentication from "../../_hooks/useUserAuthentication";
import { ChatIcon, PathwayIconWrapperButton } from "../../_UI/StyledComponents/PathwayIcons";
import UserDropdownMenu from "./UserDropdownMenu";
import OrganizationDropdownMenu from "./OrganizationDropdownMenu";
import useSupportChat from "../../_hooks/useSupportChat";

export const DefaultHeader = () => {
    const user = useSelector((state) => state.authentication.user);

    const navigate = useNavigate();

    const { isSuperUser } = useUserAuthentication();

    const { openSupportChat } = useSupportChat();

    const { environment, color } = useEnvironment();

    const handleChatToggle = useCallback(() => {
        if (typeof window !== "undefined" && typeof window.Intercom === "function") {
            openSupportChat();
            return;
        }
    }, [openSupportChat]);

    useEffect(() => {
        if (user && user.loggedIn && !user.selectedOrganization) {
            navigate("/dologout");
        }
    }, [user, navigate]);

    return (
        <Navbar color="light-subtle" light expand="lg" className="pb-0 pl-0 pr-0">
            <HeaderLayout className="pl-0 pr-0">
                <LeftLayout>
                    <CustomToggler id="sidebar-toggler" className="d-block d-md-none" style={{ display: "block" }} />

                    <div>
                        {isSuperUser && environment != null && color !== null ? (
                            <StyledLink to="/" className={color}>
                                <StyledText className={color}>{environment}</StyledText>
                            </StyledLink>
                        ) : (
                            <Link to="/">
                                <img height="49" alt="pathway-solutions logo" src="/logo.png" />
                            </Link>
                        )}
                    </div>

                    <PathwayIconWrapperButton onClick={handleChatToggle} data-cy="feedback-chat-button">
                        <ChatIcon size="1.8rem" color="var(--dark)" />
                    </PathwayIconWrapperButton>
                </LeftLayout>

                <AlertWrapper />

                <RightLayout navbar>
                    <UserDropdownMenu />
                    <OrganizationDropdownMenu />
                </RightLayout>
            </HeaderLayout>
        </Navbar>
    );
};

const HeaderLayout = styled.div`
    display: grid;
    width: 100%;
    justify-content: space-between;
    column-gap: var(--gap-xxxl);
    grid-template-columns: max-content max-content;
    border-bottom: 1px solid var(--bs-dark-bg-subtle);
    padding-bottom: 0 !important;
`;

const LeftLayout = styled.div`
    display: grid;
    grid-template-columns: repeat(3, min-content);
    align-items: center;
    column-gap: var(--gap-xl);
    grid-template-rows: 1fr;
`;

const RightLayout = styled(Nav)`
    display: grid;
    justify-items: right;
    align-content: space-evenly;
`;

const AlertWrapper = () => {
    const alert = useSelector((state) => state.alert);

    return (
        <>
            {alert && alert.message && (
                <AlertContainer>
                    <MessageWrapper>
                        {alert.type === "general-error" && <ErrorMessage marginless>{alert.message}</ErrorMessage>}

                        {alert.type === "general-success" && (
                            <SuccessMessage marginless>{alert.message}</SuccessMessage>
                        )}
                    </MessageWrapper>
                </AlertContainer>
            )}
        </>
    );
};

const CustomToggler = styled(NavbarToggler)`
    outline: none !important;
    border: none !important;

    &:focus {
        box-shadow: none !important;
    }
`;

const AlertContainer = styled.div`
    display: grid;
    position: absolute;
    z-index: 9;
    padding-bottom: 10px;
    width: 100%;
    justify-items: center;
    text-align: center;
    top: 0;
    left: 0;
`;

const MessageWrapper = styled.div`
    position: absolute;
    z-index: auto;
`;

const StyledLink = styled(Link)`
    display: block;
    text-align: center;
    padding: 10px;
    border-radius: 5px;
    text-decoration: none !important;
    ${({ colorClass }) => colorClass};

    &:hover > * {
        color: lightgrey !important;
    }
`;

const StyledText = styled.h2`
    font-size: 1.5em;
    font-weight: bold;
    margin: 0;
    color: white !important;

    ${({ colorClass }) => colorClass};
`;
